import { getFileExtension, sanitizeFileUrl } from "./filePreview.js";
import {
  fetchProtectedPreviewUrl,
  isProtectedPreviewUrl,
} from "./exportPdfPreview.js";
import { preferPdfExportActionSheet } from "./pdfExportUi.js";

/** 1×1 PNG – gültig für canShare-Probes auf iOS/Android */
const MINIMAL_PNG_BYTES = Uint8Array.from(
  atob(
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==",
  ),
  (c) => c.charCodeAt(0),
);

const MIME_BY_EXT = Object.freeze({
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  zip: "application/zip",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
});

function isBlobUrl(url = "") {
  return String(url || "").startsWith("blob:");
}

export function sanitizeShareFileName(name = "datei") {
  const base = String(name || "datei").replace(/[\r\n"/\\]/g, "_").trim();
  return base || "datei";
}

export function mimeTypeForFileShare(ext = "", blobType = "") {
  const fromBlob = String(blobType || "").trim().toLowerCase();
  if (fromBlob && fromBlob !== "application/octet-stream") return fromBlob;
  const e = String(ext || "").toLowerCase().replace(/^\./, "");
  return MIME_BY_EXT[e] || "application/octet-stream";
}

export function isNativeFileShareSupported() {
  if (typeof navigator === "undefined") return false;
  return typeof navigator.share === "function";
}

export function canNativeShareFile(file) {
  if (!isNativeFileShareSupported() || !file) return false;
  if (typeof navigator.canShare !== "function") return true;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/**
 * Prüft, ob das Gerät typischerweise Datei-Sharing unterstützt (ohne Netzwerk).
 */
function probeBytesForExt(ext = "") {
  const e = String(ext || "").toLowerCase().replace(/^\./, "");
  if (e === "pdf") return new Uint8Array([0x25, 0x50, 0x44, 0x46]);
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(e)) return MINIMAL_PNG_BYTES;
  return new Uint8Array([0]);
}

function shareProbeFileName(fileName = "", extHint = "") {
  const safeName = sanitizeShareFileName(fileName);
  const ext = String(extHint || "").replace(/^\./, "") || getFileExtension(safeName);
  if (!ext) return safeName;
  if (getFileExtension(safeName)) return safeName;
  return `${safeName}.${ext}`;
}

export function isSecureShareContext() {
  if (typeof window === "undefined") return false;
  return Boolean(window.isSecureContext);
}

export function probeNativeFileShare(fileName = "datei.pdf", extHint = "") {
  if (!isNativeFileShareSupported() || !isSecureShareContext()) return false;
  const safeName = shareProbeFileName(fileName, extHint);
  const ext = getFileExtension(safeName) || String(extHint || "").replace(/^\./, "");
  const mime = mimeTypeForFileShare(ext);
  try {
    const file = new File([probeBytesForExt(ext)], safeName, { type: mime });
    return canNativeShareFile(file);
  } catch {
    return false;
  }
}

/**
 * Ob der Teilen-Button angezeigt werden soll.
 * Mobile: optimistisch (echte Datei wird erst beim Tap geprüft).
 */
export function shouldOfferNativeFileShare(
  fileName = "datei",
  { extHint = "", preferMobile } = {},
) {
  if (!isNativeFileShareSupported() || !isSecureShareContext()) return false;
  const mobile =
    preferMobile === true ||
    (preferMobile !== false && preferPdfExportActionSheet());
  if (mobile) return true;
  return probeNativeFileShare(fileName, extHint);
}

async function resolveBlobForShare(
  url = "",
  { existingBlobUrl = "", signal } = {},
) {
  let blobUrl = isBlobUrl(existingBlobUrl) ? existingBlobUrl : "";

  if (!blobUrl) {
    const safe = sanitizeFileUrl(url);
    if (!safe) return null;
    if (isProtectedPreviewUrl(safe)) {
      blobUrl = await fetchProtectedPreviewUrl(safe, { signal });
    } else if (safe.startsWith("blob:") || /^https?:/i.test(safe)) {
      blobUrl = safe;
    } else if (safe.startsWith("/")) {
      const res = await fetch(safe, { credentials: "include", signal });
      if (!res.ok) return null;
      return res.blob();
    } else {
      return null;
    }
  }

  if (!blobUrl) return null;

  const res = await fetch(blobUrl, { signal });
  if (!res.ok) return null;
  return res.blob();
}

/**
 * Teilt eine geschützte Datei über das native Share-Sheet (Mail, WhatsApp, …).
 * @returns {Promise<{ ok: boolean, reason?: "unsupported"|"cancelled"|"fetch"|"error" }>}
 */
export async function shareProtectedFile(
  url = "",
  fileName = "datei",
  {
    existingBlobUrl = "",
    existingBlob = null,
    shareTitle = "",
    shareText = "",
    signal,
  } = {},
) {
  if (!isNativeFileShareSupported()) {
    return { ok: false, reason: "unsupported" };
  }

  const safeName = sanitizeShareFileName(fileName);
  const ext = getFileExtension(safeName);

  let blob = existingBlob instanceof Blob ? existingBlob : null;
  try {
    if (!blob) {
      blob = await resolveBlobForShare(url, { existingBlobUrl, signal });
    }
  } catch {
    return { ok: false, reason: "fetch" };
  }

  if (!blob) return { ok: false, reason: "fetch" };

  const mime = mimeTypeForFileShare(ext, blob.type);
  let file;
  try {
    file = new File([blob], safeName, { type: mime });
  } catch {
    return { ok: false, reason: "unsupported" };
  }

  const payload = { files: [file] };
  if (typeof navigator.canShare === "function" && !canNativeShareFile(file)) {
    return { ok: false, reason: "unsupported" };
  }
  const title = String(shareTitle || "").trim();
  const text = String(shareText || "").trim();
  if (title) payload.title = title;
  if (text) payload.text = text;

  try {
    await navigator.share(payload);
    return { ok: true };
  } catch (err) {
    if (err?.name === "AbortError") return { ok: false, reason: "cancelled" };
    return { ok: false, reason: "error" };
  }
}
